import { ALERT_NOTIFICATION_TYPES } from './constants';
import { COMMUNITY_FIND_CLUB_PATH, COMMUNITY_FIND_PROCESSOR_PATH } from '../community/constants/communityRoutes';
import { TOOLKIT_PRINTABLE_PATH } from '../toolkit/constants/toolkitRoutes';
import { isCampEssentialsEnabled } from '../../utils/featureFlags';

export function formatNotificationRelative(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return 'yesterday';
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function isAlertNotification(type) {
  return ALERT_NOTIFICATION_TYPES.has(type);
}

function asPositiveId(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function clubFeedNotificationTarget(notification) {
  const n = notification || {};
  const meta = n.metadata && typeof n.metadata === 'object' ? n.metadata : {};
  const clubId = asPositiveId(n.club_id) || asPositiveId(meta.club_id);
  const stationId = asPositiveId(n.hunt_location_id) || asPositiveId(meta.station_id);
  if (!clubId || !stationId) return null;
  return { clubId, stationId };
}

export function clubPlanSharedTarget(notification) {
  const n = notification || {};
  const meta = n.metadata && typeof n.metadata === 'object' ? n.metadata : {};
  const clubId = asPositiveId(n.club_id) || asPositiveId(meta.club_id);
  const planId = asPositiveId(meta.area_group_id);
  if (!clubId || !planId) return null;
  return { clubId, planId };
}

export function getNotificationDestination(notification) {
  const n = notification || {};
  if (n.type === 'feeder_low') {
    return '/toolkit/feed-management';
  }
  if (n.type === 'club_feed_filled' || n.type === 'club_feed_low') {
    const target = clubFeedNotificationTarget(n);
    if (target) return `/clubs/${target.clubId}/map?standId=${target.stationId}`;
  }
  if (n.type === 'club_plan_shared') {
    const target = clubPlanSharedTarget(n);
    if (target) return `/clubs/${target.clubId}?tab=overview&planId=${target.planId}`;
  }
  if (n.type === 'listing_expiring_soon' && n.community_listing_id) {
    if (!isCampEssentialsEnabled()) {
      return '/notifications';
    }
    return `/camp-essentials/gear/${n.community_listing_id}`;
  }
  if (n.type === 'club_join_request' && n.club_id) {
    return {
      pathname: `/clubs/${n.club_id}`,
      search: '?tab=admin',
      state: { focusJoinRequests: true },
    };
  }
  if (n.type === 'club_join_decision') {
    if (n.metadata?.decision === 'approved') {
      const redirect = n.metadata?.redirect;
      if (typeof redirect === 'string' && redirect.startsWith('/')) {
        return redirect;
      }
      if (n.club_id) {
        return `/clubs/${n.club_id}/rules?join=1`;
      }
    }
    if (n.metadata?.decision === 'declined') {
      return COMMUNITY_FIND_CLUB_PATH;
    }
  }
  if (n.type === 'club_member_invite') {
    return {
      pathname: '/notifications',
      state: { focusClubInvites: true },
    };
  }
  if (n.type === 'club_member_invite_decision') {
    const clubId = n.club_id || n.metadata?.club_id;
    if (clubId) {
      return {
        pathname: `/clubs/${clubId}`,
        search: '?tab=members',
      };
    }
  }
  if (n.type === 'processor_listing_approved' || n.type === 'processor_listing_rejected') {
    return COMMUNITY_FIND_PROCESSOR_PATH;
  }
  if (n.type === 'map_print_status') {
    if (n.metadata?.status === 'ready' && n.metadata?.job_id) {
      return { downloadPrintJobId: n.metadata.job_id };
    }
    return TOOLKIT_PRINTABLE_PATH;
  }
  if (n.type === 'status_update' || n.type === 'hunt_sign_out' || n.type === 'hunt_sign_in' || n.type === 'bio_update') {
    if (n.actor?.id) return `/users/${n.actor.id}`;
  }
  if (n.type === 'photo_posted' || n.type === 'photo_comment') {
    const ownerId = n.owner_user_id || n.actor?.id;
    if (ownerId) {
      return {
        pathname: `/users/${ownerId}`,
        state: { openHarvestId: n.harvest_id },
      };
    }
  }
  return null;
}

export function getNotificationSummary(notification) {
  const n = notification || {};
  if (n.text) return n.text;

  const actorName = n.actor?.profile?.first_name
    ? `${n.actor.profile.first_name} ${n.actor.profile.last_name || ''}`.trim()
    : n.actor?.username
      ? `@${n.actor.username}`
      : 'Someone';

  switch (n.type) {
    case 'feeder_low':
      return n.metadata?.feeder_name
        ? `${n.metadata.feeder_name} needs attention`
        : 'Feeder needs attention';
    case 'hunt_sign_out':
      return `${actorName} signed out for ${n.time_period || 'a hunt'}`;
    case 'hunt_sign_in':
      return `${actorName} signed back in`;
    case 'status_update':
      return `${actorName} updated status`;
    case 'bio_update':
      return `${actorName} updated bio`;
    case 'photo_posted':
      return `${actorName} posted a photo`;
    case 'photo_comment':
      return `${actorName} commented on a photo`;
    case 'listing_expiring_soon':
      return 'Community listing expiring soon';
    case 'club_join_request':
      return n.text || 'New membership request';
    case 'club_join_decision':
      if (n.metadata?.decision === 'approved') {
        return n.text || 'Your club membership request was approved';
      }
      if (n.metadata?.decision === 'declined') {
        return n.text || 'Your club membership request was declined';
      }
      return n.text || 'Club membership decision';
    case 'club_member_invite':
      return n.text || 'You were invited to join a club';
    case 'club_member_invite_decision':
      if (n.metadata?.decision === 'accepted') {
        return n.text || 'Your club invite was accepted';
      }
      if (n.metadata?.decision === 'declined') {
        return n.text || 'Your club invite was declined';
      }
      return n.text || 'Club invite decision';
    case 'processor_listing_approved':
      return n.text || 'Your processor listing is live';
    case 'processor_listing_rejected':
      return n.text || "We couldn't list that processor";
    case 'map_print_status':
      if (n.metadata?.status === 'failed') {
        return n.text || 'Your printable map could not be built';
      }
      return n.text || 'Your printable map is ready to download';
    default:
      return 'Notification';
  }
}

export function getNotificationActorLabel(notification, usdiUsername = 'usdi_yona') {
  const username = notification?.actor?.username;
  if (username === usdiUsername) {
    const first = notification?.actor?.profile?.first_name || 'Usdi Yona';
    const last = notification?.actor?.profile?.last_name || '';
    return `${first} ${last}`.trim();
  }
  if (notification?.actor?.profile?.first_name) {
    return `${notification.actor.profile.first_name} ${notification.actor.profile.last_name || ''}`.trim();
  }
  return notification?.actor?.username ? `@${notification.actor.username}` : 'Yona';
}
